import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: 'website' | 'article' | 'video.other';
  ogImage?: string;
  ogImageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
}

const SITE_NAME = 'Auto News Weekly';
const SITE_URL = 'https://autonewsweekly.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

// Helper to ensure absolute URLs for OG images
function ensureAbsoluteUrl(url: string | undefined): string {
  if (!url) return DEFAULT_OG_IMAGE;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If it's a relative URL, make it absolute
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function SEOHead({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  publishedTime,
  modifiedTime,
  author,
  section,
  tags = [],
  noindex = false,
}: SEOHeadProps) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : SITE_URL);
  const absoluteOgImage = ensureAbsoluteUrl(ogImage);
  
  // Track created meta tags for cleanup
  const createdTagsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set/update meta tags
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
        createdTagsRef.current.push(meta);
      }
      meta.content = content;
    };

    // Helper to set/update link tags
    const setLink = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
        createdTagsRef.current.push(link);
      }
      link.href = href;
    };

    // Basic meta tags
    setMeta('description', description);
    setMeta('author', author || SITE_NAME);
    
    // Robots
    if (noindex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Canonical
    setLink('canonical', canonicalUrl);

    // Open Graph - CRITICAL for LinkedIn
    setMeta('og:type', ogType, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', absoluteOgImage, true);
    setMeta('og:image:width', '1200', true);
    setMeta('og:image:height', '630', true);
    if (ogImageAlt) setMeta('og:image:alt', ogImageAlt, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'en_US', true);

    // Article-specific OG tags
    if (ogType === 'article') {
      if (publishedTime) setMeta('article:published_time', publishedTime, true);
      if (modifiedTime) setMeta('article:modified_time', modifiedTime, true);
      if (author) setMeta('article:author', author, true);
      if (section) setMeta('article:section', section, true);
      tags.forEach((tag, i) => {
        setMeta(`article:tag:${i}`, tag, true);
      });
    }

    // Twitter Card - CRITICAL for Twitter/X previews
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@AutoNewsWeekly');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', absoluteOgImage);
    if (ogImageAlt) setMeta('twitter:image:alt', ogImageAlt);

    // Cleanup function - reset to defaults when component unmounts
    return () => {
      // Reset title
      document.title = `${SITE_NAME} - Automotive News & Reviews`;
      
      // Reset OG tags to defaults
      const resetMeta = (name: string, defaultValue: string, property = false) => {
        const attr = property ? 'property' : 'name';
        const meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
        if (meta) meta.content = defaultValue;
      };
      
      resetMeta('og:type', 'website', true);
      resetMeta('og:title', `${SITE_NAME} - Automotive News & Reviews`, true);
      resetMeta('og:description', 'Your weekly source for automotive news, reviews, comparisons, and industry events.', true);
      resetMeta('og:url', SITE_URL, true);
      resetMeta('og:image', DEFAULT_OG_IMAGE, true);
      resetMeta('twitter:title', `${SITE_NAME} - Automotive News & Reviews`);
      resetMeta('twitter:description', 'Your weekly source for automotive news, reviews, comparisons, and industry events.');
      resetMeta('twitter:image', DEFAULT_OG_IMAGE);
    };
  }, [fullTitle, description, canonicalUrl, ogType, absoluteOgImage, ogImageAlt, publishedTime, modifiedTime, author, section, tags, noindex]);

  return null;
}

export function generateMetaDescription(text: string, maxLength = 155): string {
  if (!text) return '';
  // Strip HTML tags
  const stripped = text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  if (stripped.length <= maxLength) return stripped;
  return stripped.substring(0, maxLength - 3).trim() + '...';
}
